import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core-input';
import '@maersk-global/mds-components-core-button';
import '@maersk-global/mds-components-core-toast';
import '@maersk-global/mds-components-core-notification';
import { ScoreBand, VscKpiItem } from '../../../models/vsc-settings.model';
import { VscKpiCategoryService } from '../../../services/vsc-kpi-category.service';
import { AuthHelper } from '../../../auth/auth-helper';

const UNIT_SUFFIX: Record<string, string> = {
  '%': '%',
  hours: 'h',
  days: 'd',
};

const SCORE_COLORS: Record<number, { bg: string; text: string }> = {
  1: {
    bg: 'var(--mds_brand_appearance_error_default_background-color)',
    text: 'var(--mds_brand_appearance_error_default_on-background-color)',
  },
  2: {
    bg: 'var(--mds_brand_appearance_warning_default_background-color)',
    text: 'var(--mds_brand_appearance_warning_default_on-background-color)',
  },
  3: {
    bg: 'var(--mds_brand_appearance_info_default_background-color)',
    text: 'var(--mds_brand_appearance_info_default_on-background-color)',
  },
  4: {
    bg: 'var(--mds_brand_appearance_secondary_default_background-color)',
    text: 'var(--mds_brand_appearance_secondary_default_on-background-color)',
  },
  5: {
    bg: 'var(--mds_brand_appearance_success_default_background-color)',
    text: 'var(--mds_brand_appearance_success_default_on-background-color)',
  },
};

@Component({
  selector: 'app-vsc-kpi-binning-table',
  imports: [CommonModule],
  templateUrl: './vsc-kpi-binning-table.component.html',
  styleUrl: './vsc-kpi-binning-table.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscKpiBinningTableComponent implements OnInit, OnChanges {
  @Input() kpis: VscKpiItem[] = [];
  @Input() categoryId = '';
  @Input() showUnit = false;
  @Input() readonly = false;
  @Input() disabledIds: string[] = [];
  @Input() maxScore5: Record<string, string> = {};
  @Input() hideSave = false;
  @Input() toastMessage = 'KPI score binning saved.';

  /**
   * Requires a complete set of ranges on every row that is not disabled: a category carrying weight
   * has to score, so a half-filled row is an error. Only score 1's open end may be left blank.
   */
  @Input() requireCompleteRanges = false;

  /**
   * The edited bands, keyed by KPI id, for a parent that owns the Save (`hideSave`). Nothing is
   * posted from here in that mode — the parent sends them with the rest of its payload.
   *
   * Async, because a row can also settle during change detection (a weight change clears it): a
   * synchronous emit would move the parent's Save state after its bindings were already checked.
   */
  @Output() readonly bandsChange = new EventEmitter<Record<string, ScoreBand[]>>(true);

  /** `true` while any range in the table is invalid, so the parent can block its Save. */
  @Output() readonly validityChange = new EventEmitter<boolean>(true);

  @ViewChild('toastRef') toastRef!: ElementRef;

  private readonly kpiCategoryService = inject(VscKpiCategoryService);

  readonly scores = [1, 2, 3, 4, 5];
  /** The score whose max the parent may lock to the category weight. */
  readonly topScore = 5;
  readonly unitOptions = ['None', '%', 'hours', 'days'];
  readonly scoreColors = SCORE_COLORS;

  bins: Record<string, string> = {};
  units: Record<string, string> = {};
  isSaving = false;

  private originalBins: Record<string, string> = {};

  private hasValidDecimalFormat(value: string): boolean {
    if (value === '') return true;
    return /^-?\d+(\.\d)?$/.test(value);
  }

  private isNextMinBoundaryInvalid(kpiId: string, score: number, numVal: number): boolean {
    const prevMax = this.bins[`${kpiId}_${score - 1}_max`];
    if (prevMax === '' || !this.hasValidDecimalFormat(prevMax)) return false;
    const expected = Math.round((Number(prevMax) + 0.1) * 10) / 10;
    return Math.round(numVal * 10) / 10 !== expected;
  }

  private isNextMaxBoundaryInvalid(kpiId: string, score: number, numVal: number): boolean {
    const prevMin = this.bins[`${kpiId}_${score - 1}_min`];
    if (prevMin === '' || !this.hasValidDecimalFormat(prevMin)) return false;
    const expected = Math.round((Number(prevMin) - 0.1) * 10) / 10;
    return Math.round(numVal * 10) / 10 !== expected;
  }

  private isBoundaryInvalid(kpiId: string, score: number, field: 'min' | 'max', numVal: number): boolean {
    if (score <= 1) return false;
    const lowerBetter = this.isKpiLowerBetter(kpiId);
    if (!lowerBetter && field === 'min') return this.isNextMinBoundaryInvalid(kpiId, score, numVal);
    if (lowerBetter && field === 'max') return this.isNextMaxBoundaryInvalid(kpiId, score, numVal);
    return false;
  }

  /**
   * The locked score-5 max is the highest score the row can award, so it caps every other value in
   * that row too — a band of 15–18 under a ceiling of 5 is unreachable, not merely unusual.
   */
  private getRowCeiling(kpiId: string): number | null {
    const locked = this.maxScore5[kpiId];
    if (locked === undefined || locked === '' || !this.hasValidDecimalFormat(locked)) return null;
    return Number(locked);
  }

  /** The max in force for a cell: the locked one where the parent supplies it, else what was typed. */
  private getEffectiveMax(kpiId: string, score: number): number | null {
    const raw = this.getLockedMax(kpiId, score) ?? this.bins[`${kpiId}_${score}_max`];
    if (raw === undefined || raw === '' || !this.hasValidDecimalFormat(raw)) return null;
    return Number(raw);
  }

  /**
   * The lowest band runs off the scale — everything below its max scores 1 (above its min, when
   * lower is better) — so that one end stays open. Every other cell has to carry a number.
   */
  private isMissingRequiredValue(kpiId: string, score: number, field: 'min' | 'max'): boolean {
    if (!this.requireCompleteRanges) return false;
    const openField = this.isKpiLowerBetter(kpiId) ? 'max' : 'min';
    return !(score === 1 && field === openField);
  }

  isInputInvalid(kpiId: string, score: number, field: 'min' | 'max'): boolean {
    if (this.isDisabled(kpiId)) return false;
    if (field === 'max' && this.getLockedMax(kpiId, score) !== null) return false;

    const value = this.bins[`${kpiId}_${score}_${field}`];
    if (value === '') return this.isMissingRequiredValue(kpiId, score, field);
    if (!this.hasValidDecimalFormat(value)) return true;

    const numVal = Number(value);

    const ceiling = this.getRowCeiling(kpiId);
    if (ceiling !== null && numVal > ceiling) return true;

    const minVal = this.bins[`${kpiId}_${score}_min`] === '' ? null : Number(this.bins[`${kpiId}_${score}_min`]);
    const maxVal = this.getEffectiveMax(kpiId, score);

    if (minVal !== null && maxVal !== null && maxVal <= minVal) return true;

    return this.isBoundaryInvalid(kpiId, score, field, numVal);
  }

  get hasAnyValidationError(): boolean {
    return this.kpis.some((kpi) =>
      this.scores.some((score) => this.isInputInvalid(kpi.id, score, 'min') || this.isInputInvalid(kpi.id, score, 'max')),
    );
  }

  ngOnInit(): void {
    this.initState();
    this.publish();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A new KPI list carries its own saved bands, so it wins over any weight-driven reset that
    // arrives in the same cycle — otherwise loading a profile would wipe the bands it just brought.
    if (changes['kpis']) {
      this.initState();
      this.blankDisabledRows();
      this.publish();
      return;
    }

    if (changes['maxScore5']) this.resetRowsWithChangedCeiling(changes['maxScore5']);
    if (changes['disabledIds']) this.blankDisabledRows();
    if (changes['maxScore5'] || changes['disabledIds']) this.publish();
  }

  /** Hands the current bands and validity to a parent that owns the Save. */
  private publish(): void {
    this.bandsChange.emit(Object.fromEntries(this.kpis.map((kpi) => [kpi.id, this.buildSaveBands(kpi.id)])));
    this.validityChange.emit(this.hasAnyValidationError);
  }

  /**
   * A category weighted 0 cannot score at all, so its row holds nothing rather than stale bands —
   * empty here, and null in what gets saved. Zeroes would claim a band of 0–0 that never applies.
   */
  private blankDisabledRows(): void {
    const bins = { ...this.bins };
    let changed = false;

    for (const kpi of this.kpis) {
      if (!this.isDisabled(kpi.id)) continue;

      for (const score of this.scores) {
        for (const field of ['min', 'max'] as const) {
          const key = `${kpi.id}_${score}_${field}`;
          if (bins[key] === '') continue;
          bins[key] = '';
          changed = true;
        }
      }
    }

    if (changed) this.bins = bins;
  }

  /**
   * Re-weighting a category moves the ceiling, which invalidates every band underneath it. The row
   * is cleared back to the new score-5 max so the ranges are re-entered against the weight in force.
   */
  private resetRowsWithChangedCeiling(change: SimpleChange): void {
    if (change.firstChange) return;

    const previous = (change.previousValue ?? {}) as Record<string, string>;
    const current = (change.currentValue ?? {}) as Record<string, string>;

    const bins = { ...this.bins };
    let changed = false;

    for (const kpi of this.kpis) {
      if (previous[kpi.id] === current[kpi.id]) continue;

      for (const score of this.scores) {
        bins[`${kpi.id}_${score}_min`] = '';
        bins[`${kpi.id}_${score}_max`] = '';
      }
      bins[`${kpi.id}_${this.topScore}_max`] = current[kpi.id] ?? '';
      changed = true;
    }

    if (changed) this.bins = bins;
  }

  private initState(): void {
    const bins: Record<string, string> = {};
    const units: Record<string, string> = {};
    for (const kpi of this.kpis) {
      for (const score of this.scores) {
        const band = kpi.scoreBands?.find((b) => b.score === score);
        bins[`${kpi.id}_${score}_min`] = band?.min == null ? '' : String(band.min);
        bins[`${kpi.id}_${score}_max`] = band?.max == null ? '' : String(band.max);
      }
      units[kpi.id] = kpi.unit && this.unitOptions.includes(kpi.unit) ? kpi.unit : 'None';
    }
    this.bins = bins;
    this.originalBins = { ...bins };
    this.units = units;
  }

  private getDirtyKpiIds(): string[] {
    return this.kpis
      .filter((kpi) =>
        this.scores.some(
          (score) =>
            this.bins[`${kpi.id}_${score}_min`] !== this.originalBins[`${kpi.id}_${score}_min`] ||
            this.bins[`${kpi.id}_${score}_max`] !== this.originalBins[`${kpi.id}_${score}_max`],
        ),
      )
      .map((kpi) => kpi.id);
  }

  private buildSaveBands(kpiId: string): ScoreBand[] {
    return this.scores.map((score) => {
      const min = this.bins[`${kpiId}_${score}_min`];
      // The locked max is what the row actually shows, so it is what gets saved.
      const max = this.getLockedMax(kpiId, score) ?? this.bins[`${kpiId}_${score}_max`];
      return {
        score,
        min: min === '' ? null : Number(min),
        max: max === '' ? null : Number(max),
      };
    });
  }

  get hasMeasurement(): boolean {
    return !this.showUnit && this.kpis.some((k) => !!k.measurement);
  }

  isDisabled(kpiId: string): boolean {
    return this.disabledIds.includes(kpiId);
  }

  isKpiLowerBetter(kpiId: string): boolean {
    return this.kpis.find((k) => k.id === kpiId)?.isLowerBetter ?? false;
  }

  getLockedMax(kpiId: string, score: number): string | null {
    if (score === this.topScore && this.maxScore5[kpiId] !== undefined && this.maxScore5[kpiId] !== '') {
      return this.maxScore5[kpiId];
    }
    return null;
  }

  getSuffix(kpiId: string): string {
    return this.showUnit ? (UNIT_SUFFIX[this.units[kpiId]] ?? '') : '';
  }

  isOddRow(index: number): boolean {
    return index % 2 !== 0;
  }

  onMinChange(kpiId: string, score: number, event: Event): void {
    if (this.readonly || this.isDisabled(kpiId)) return;
    const value = (event.target as HTMLInputElement).value;
    if (value === '' || /^-?(?:\d+(?:\.\d*)?|\.\d*)$/.test(value)) {
      this.bins = { ...this.bins, [`${kpiId}_${score}_min`]: value };
      this.publish();
    }
  }

  onMaxChange(kpiId: string, score: number, event: Event): void {
    if (this.readonly || this.isDisabled(kpiId) || this.getLockedMax(kpiId, score) !== null) return;
    const value = (event.target as HTMLInputElement).value;
    if (value === '' || /^-?(?:\d+(?:\.\d*)?|\.\d*)$/.test(value)) {
      this.bins = { ...this.bins, [`${kpiId}_${score}_max`]: value };
      this.publish();
    }
  }

  save(): void {
    if (this.readonly || this.hasAnyValidationError) return;
    const dirtyIds = this.getDirtyKpiIds();

    if (dirtyIds.length === 0) {
      this.toastRef?.nativeElement?.show();
      return;
    }

    const kpiScoreBands = dirtyIds.map((kpiId) => ({
      kpiId: Number(kpiId),
      scoreBands: this.buildSaveBands(kpiId),
    }));

    this.isSaving = true;
    this.kpiCategoryService
      .saveKpiScoreBands(this.categoryId, {
        createdById: AuthHelper.user?.userId ?? 0,
        kpiScoreBands,
      })
      .subscribe({
        next: () => {
          this.originalBins = { ...this.bins };
          this.isSaving = false;
          this.toastRef?.nativeElement?.show();
        },
        error: () => {
          this.isSaving = false;
        },
      });
  }
}
